import { useMemo, useState } from "react";

type TUseConditionalQuestionnaire = {
	questions: (PopulatedQuestion & {
		answer: TAnswer | undefined;
	})[];
};

const useConditionalQuestionnaire = ({ questions }: TUseConditionalQuestionnaire) => {
	const [renderedQuestions, setRenderedQuestions] = useState<Record<string, string[]>>({});

	const questionWithAnswerMap = useMemo(() => {
		return questions.reduce((prev, curr) => {
			prev[curr._id] = curr;
			return prev;
		}, {} as Record<string, (typeof questions)[0]>);
	}, [questions]);

	const getUpdatedAllQuestionsBasedOnRenders = (questionId: string, value: string | string[]) => {
		const currentQuestion = questionWithAnswerMap[questionId];
		if (currentQuestion === undefined) return questions;
		const currentAnswer = typeof value === "string" ? value : value.join(",");

		renderedQuestions[questionId]?.forEach((id) => {
			const q = questionWithAnswerMap[id];
			if (q) {
				q.is_root = false;
			} else {
				console.warn(`QuestionId: ${id} was not found in QuestionnaireState`, renderedQuestions);
			}
		});

		setRenderedQuestions((prev) => ({
			...prev,
			[questionId]: []
		}));

		const renderQuestions = (questionsIds: string[]) => {
			setRenderedQuestions((prev) => ({
				...prev,
				[questionId]: questionsIds
			}));
			questionsIds.forEach((id) => {
				const q: PopulatedQuestion | undefined = questionWithAnswerMap[id];
				if (q) {
					q.is_root = true;
					const answer = questionWithAnswerMap[id]?.answer;
					if (answer) {
						getUpdatedAllQuestionsBasedOnRenders(id, answer.answer);
					}
				} else {
					console.warn(`QuestionId: ${id} was not found in QuestionnaireState`);
				}
			});
		};

		currentQuestion.renders.forEach((render) => {
			switch (render.operation) {
				case "answers":
					if (currentAnswer) {
						renderQuestions(render.question_ids);
					}
					break;
				case "eq":
					if (currentAnswer === render.value) {
						renderQuestions(render.question_ids);
					}
					break;
				case "neq":
					if (currentAnswer !== render.value) {
						renderQuestions(render.question_ids);
					}
					break;
				case "gt":
					if (parseInt(currentAnswer) > parseInt(render.value)) {
						renderQuestions(render.question_ids);
					}
					break;
				case "gte":
					if (parseInt(currentAnswer) >= parseInt(render.value)) {
						renderQuestions(render.question_ids);
					}
					break;
				case "lt":
					if (parseInt(currentAnswer) < parseInt(render.value)) {
						renderQuestions(render.question_ids);
					}
					break;
				case "lte":
					if (parseInt(currentAnswer) <= parseInt(render.value)) {
						renderQuestions(render.question_ids);
					}
					break;
				case "includes":
					if (String(currentAnswer).includes(render.value)) {
						renderQuestions(render.question_ids);
					}
					break;
				case "nincludes":
					if (!String(currentAnswer).includes(render.value)) {
						renderQuestions(render.question_ids);
					}
					break;
				default:
					break;
			}
		});

		return Object.values(questionWithAnswerMap);
	};

	return {
		getUpdatedAllQuestionsBasedOnRenders
	};
};

export default useConditionalQuestionnaire;
